import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Shared UI atoms used across all auth screens.
 * Kept in one file to avoid bloat during scaffolding.
 */

export const ScreenHeader = ({ title, onBack, right, testId = "screen-header" }) => {
    const navigate = useNavigate();
    const handleBack = onBack ?? (() => navigate(-1));
    return (
        <header
            className="flex items-center justify-between px-4 pt-[max(16px,env(safe-area-inset-top))] pb-3"
            data-testid={testId}
        >
            <button
                onClick={handleBack}
                data-testid="header-back-btn"
                className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95 transition-[transform,background-color] duration-150"
                aria-label="Back"
            >
                <ArrowLeft size={20} />
            </button>
            <h1 className="font-display text-[15px] font-semibold tracking-tight">{title}</h1>
            <div className="h-10 w-10 flex items-center justify-center">{right}</div>
        </header>
    );
};

export const TextField = ({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    autoFocus,
    autoComplete,
    inputMode,
    error,
    testId,
    right,
    maxLength,
    disabled,
    onKeyDown,
}) => {
    return (
        <label className="block" data-testid={testId ? `${testId}-field` : undefined}>
            {label && (
                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                    {label}
                </span>
            )}
            <span
                className={[
                    "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
                    error
                        ? "border-destructive bg-destructive/5"
                        : "border-border bg-secondary focus-within:border-primary focus-within:bg-background",
                ].join(" ")}
            >
                <input
                    data-testid={testId}
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    autoComplete={autoComplete}
                    inputMode={inputMode}
                    maxLength={maxLength}
                    disabled={disabled}
                    onKeyDown={onKeyDown}
                    className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70"
                />
                {right}
            </span>
            {error && (
                <span
                    className="block text-[12px] text-destructive mt-1.5 ml-1"
                    data-testid={testId ? `${testId}-error` : undefined}
                >
                    {error}
                </span>
            )}
        </label>
    );
};

export const PrimaryButton = ({
    children,
    onClick,
    disabled,
    loading,
    testId,
    type = "button",
    variant = "primary",
}) => {
    const styles =
        variant === "primary"
            ? "bg-primary text-primary-foreground hover:bg-primary-hover disabled:bg-primary/40"
            : "bg-secondary text-secondary-foreground hover:bg-muted";
    return (
        <button
            type={type}
            data-testid={testId}
            onClick={onClick}
            disabled={disabled || loading}
            className={[
                "w-full h-14 rounded-2xl font-medium text-[15px] shadow-soft",
                "transition-[transform,background-color,opacity] duration-200",
                "active:scale-[0.98] disabled:active:scale-100 disabled:cursor-not-allowed",
                styles,
            ].join(" ")}
        >
            {loading ? (
                <span className="inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-current animate-pulse-soft" />
                    <span className="h-2 w-2 rounded-full bg-current animate-pulse-soft" style={{ animationDelay: "200ms" }} />
                    <span className="h-2 w-2 rounded-full bg-current animate-pulse-soft" style={{ animationDelay: "400ms" }} />
                </span>
            ) : (
                children
            )}
        </button>
    );
};

export const GhostButton = ({ children, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="w-full h-12 rounded-2xl font-medium text-[14px] text-primary hover:bg-primary/5 active:scale-[0.98] transition-[transform,background-color] duration-200"
    >
        {children}
    </button>
);

/**
 * AuthScreen: scrollable container with consistent spacing.
 * Ensures the keyboard on mobile never hides the primary action button
 * because content area scrolls independently.
 */
export const AuthScreen = ({ children, testId }) => (
    <div className="screen" data-testid={testId}>
        {children}
    </div>
);

export const AuthBody = ({ children, className = "" }) => (
    <div className={`flex-1 overflow-y-auto no-scrollbar px-6 pb-6 ${className}`}>
        {children}
    </div>
);

export const AuthFooter = ({ children }) => (
    <div
        className="px-6 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] space-y-2 bg-background"
        style={{ boxShadow: "0 -16px 24px -16px hsl(var(--background))" }}
    >
        {children}
    </div>
);
